import type { UserPublic } from '@nexaclaim/shared';
import { create } from 'zustand';
import { api, configureApi } from './api';

interface AuthState {
  user: UserPublic | null;
  accessToken: string | null;
  isAuthenticated: boolean;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<string | null>;
  setUser: (user: UserPublic) => void;
  _setToken: (token: string) => void;
  _clearSession: () => void;
}

export const useAuthStore = create<AuthState & AuthActions>((set, get) => ({
  user: null,
  accessToken: null,
  isAuthenticated: false,

  setUser(user) {
    set({ user });
  },

  _setToken(token) {
    set({ accessToken: token, isAuthenticated: true });
  },

  _clearSession() {
    set({ user: null, accessToken: null, isAuthenticated: false });
  },

  async login(email, password) {
    const res = await api.auth.login(email, password);
    set({ accessToken: res.access_token, user: res.user, isAuthenticated: true });
  },

  async logout() {
    try {
      await api.auth.logout();
    } catch {
      // ignore errors on logout
    } finally {
      get()._clearSession();
    }
  },

  async refresh() {
    try {
      const res = await api.auth.refresh();
      set({ accessToken: res.access_token, isAuthenticated: true });
      return res.access_token;
    } catch {
      get()._clearSession();
      return null;
    }
  },
}));

// Configure the API client to read the token from this store and
// attempt a token refresh on 401 before giving up.
configureApi({
  getToken: () => useAuthStore.getState().accessToken,
  onUnauthorized: () => useAuthStore.getState().refresh(),
});
